import { useCallback, useState } from "react";
import MsTable from "../../../../components/Widgets/Table";
import { colType } from "../../../../Types";
import FloatAddButton from "../../../../components/Widgets/FloatAddButton";
import { Gift } from "../../../../api/giftCategories";
import useGiftOrdersQuery from "../hooks/useGiftOrdersQuery";
import GiftOrderModal from "./GiftOrderModal";
import { FaTrash } from "react-icons/fa";
import useDeleteOrderGiftMutation from "../hooks/useDeleteOrderGiftMutation";
import DSCircularProgress from "../../../system-design/DSCircularProgress";
import { useTheme } from "@mui/material";
import DSBox from "../../../system-design/DSBox";
import DSDialog from "../../../system-design/DSDialog";
import DSDialogTitle from "../../../system-design/DSDialogTitle";
import DSDialogContent from "../../../system-design/DSDialogContent";
import DSDialogActions from "../../../system-design/DSDialogActions";
import DSSecondaryButton from "../../../system-design/DSSecondaryButton";
import DSPrimaryButton from "../../../system-design/DSPrimaryButton";
import DSErrorButton from "../../../system-design/DSErrorButton";
import DSAlert from "../../../system-design/DSAlert";
import DSTextField from "../../../system-design/DSTextField";
import DSTypography from "../../../system-design/DSTypography";
import DSCheckBox from "../../../system-design/DSCheckBox";
import DSFormControlLabel from "../../../system-design/DSFormControlLabel";
import FilterBar from "components/FilterBar";
import { debounce } from "lodash";

export type GiftRow = {
  orderName: string;
  date: string;
  numOfClocks: number;
  name: string;
  image: JSX.Element;
  delete: JSX.Element;
};
function GiftOrders() {
  const [query, setQuery] = useState<string>();
  const [searchInput, setSearchInput] = useState<string>("");
  const {
    data: giftOrders,
    fetchNextPage,
    refetch,
    isLoading,
  } = useGiftOrdersQuery(query);
  const handleFreeSearch = useCallback(
    debounce((s) => setQuery(s), 500),
    [],
  );
  const theme = useTheme();
  const { mutate: deleteOrderGiftMutation } = useDeleteOrderGiftMutation();
  const [addNewOrderGift, setAddNewOrderGift] = useState<boolean>(false);
  const [giftToDelete, setGiftToDelete] = useState("");

  const tableColumns: Array<colType> = [
    { label: "order name", field: "orderName", sort: false },
    { label: "name", field: "name", sort: false },
    { label: "Author Image", field: "image", sort: false },
    { label: "Date", field: "date", sort: false },
    { label: "Number of clocks", field: "numOfClocks", sort: false },
    {
      size: 32,
      noHeadline: true,
      sort: false,
      label: "",
      field: "delete",
    },
  ];

  const tableRows = (gift: Gift): GiftRow => {
    return {
      name: gift.senderName,
      numOfClocks: gift.clocksIds?.length || 0,
      orderName: gift.orderName,
      date: gift.createdAt.toString(),
      image: (
        <img width={100} src={gift.senderImageUrl} alt={gift.senderName} />
      ),
      delete: (
        <FaTrash
          style={{ margin: "8px", cursor: "pointer" }}
          onClick={(e) => {
            e.stopPropagation();
            setGiftToDelete(gift.orderName);
          }}
        />
      ),
    };
  };

  const DeleteGiftModal = () => {
    return (
      <>
        {giftToDelete && (
          <DSDialog onClose={() => setGiftToDelete("")} open fullWidth>
            <DSDialogTitle>Confirm Deletion</DSDialogTitle>
            <DSDialogContent>
              <DSTypography>
                Are you sure you want to delete this gift ?
              </DSTypography>
            </DSDialogContent>
            <DSDialogActions>
              <DSSecondaryButton onClick={() => setGiftToDelete("")}>
                Cancel
              </DSSecondaryButton>
              <DSErrorButton
                onClick={() => {
                  deleteOrderGiftMutation(giftToDelete);
                  setGiftToDelete("");
                }}
              >
                Delete
              </DSErrorButton>
            </DSDialogActions>
          </DSDialog>
        )}
      </>
    );
  };

  const AddGiftOrderModal = () => {
    const [orderName, setOrderName] = useState("");
    const [byOrder, setByOrder] = useState(false);
    const [showWarning, setShowWarning] = useState(false);
    const addNewCategory = async () => {
      if (!byOrder && !orderName) {
        setShowWarning(true);
        return;
      }
      setShowWarning(false);
      const url = byOrder
        ? `https://studio.klydoclock.com/gift?byOrder=true`
        : `https://studio.klydoclock.com/gift?orderName=${orderName}`;
      window.open(url, "_blank", "noreferrer");
    };
    return (
      <>
        <DSDialogTitle>Add Gift to order</DSDialogTitle>
        <DSDialogContent>
          {showWarning && (
            <DSAlert severity="error" sx={{ mb: 2 }}>
              Enter Order Name to add gift to order
            </DSAlert>
          )}
          <form>
            <DSFormControlLabel
              control={
                <DSCheckBox
                  checked={byOrder}
                  onChange={(e) => setByOrder(e.target.checked)}
                />
              }
              label="Let user enter order"
              sx={{ mb: 2 }}
            />
            {!byOrder && (
              <DSTextField
                label="Order Name"
                placeholder="Enter Order Name"
                fullWidth
                value={orderName}
                onChange={(e) => setOrderName(e.target.value)}
              />
            )}
          </form>
        </DSDialogContent>
        <DSDialogActions>
          <DSSecondaryButton onClick={() => setAddNewOrderGift(false)}>
            Cancel
          </DSSecondaryButton>
          <DSPrimaryButton
            onClick={() => {
              addNewCategory();
              setAddNewOrderGift(false);
            }}
          >
            Add Gift to Order
          </DSPrimaryButton>
        </DSDialogActions>
      </>
    );
  };

  return (
    <>
      <FilterBar
        search={{
          value: searchInput,
          onChange: (value) => {
            setSearchInput(value);
            handleFreeSearch(value);
          },
          placeholder: "Search orders",
        }}
      />
      {giftOrders && !isLoading ? (
        <>
          <MsTable
            id="Greet Orders"
            data={giftOrders}
            cols={tableColumns}
            rowBuild={tableRows}
            onScrollDown={fetchNextPage}
            title="Greet Orders"
            shouldHideSearchBar
            initSort=""
            cancelSort={true}
            modal={GiftOrderModal}
          />
          <FloatAddButton onClick={() => setAddNewOrderGift(true)} />
        </>
      ) : (
        <DSBox
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: theme.zIndex.modal,
          }}
        >
          <DSCircularProgress />
        </DSBox>
      )}
      <DeleteGiftModal />
      {addNewOrderGift && (
        <DSDialog
          open={true}
          fullWidth
          maxWidth="md"
          onClose={() => {
            setAddNewOrderGift(false);
            refetch();
          }}
        >
          <AddGiftOrderModal />
        </DSDialog>
      )}
    </>
  );
}

export default GiftOrders;
